/* eslint-disable react-refresh/only-export-components */
import { RouteObject, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { UserRole } from "@/services/types/common.types";
import { Loading } from "@/shared/components/common/Loading";
import { RouteErrorBoundary } from "@/shared/components/common/RouteErrorBoundary";
import AuthLayout from "@/features/auth/layout/AuthLayout";
import { PersonalizedLearning } from "@/features/auth/components/PersonalizedLearning";
import { RoleBasedRoute } from "./RoleBasedRoute";
import { DashboardLayout } from "@/features/dashboard/components/DashboardLayout";
import { ProtectedRoute } from "./ProtectedRoute";
import { LanguageProvider } from "@/features/auth/hooks/use-language/use-language";
import { PublicRoute } from "./PublickRoute";
import { TeachersLayout } from "@/features/teachers/components/TeachersLayout";
import { StudentsLayout } from "@/features/students/components/StudentsLayout";
import { ContentCreatorsLayout } from "@/features/contentCreators/components/ContentCreatorsLayout";
import { TenantsLayout } from "@/features/school/components/TenantsLayout";
import { InstitutionLayout } from "@/features/institutions/components/InstitutionLayout";
import {
  SCHOOL_ROLES,
  INSTITUTION_ROLES,
  INSTITUTION_DENIED_ROLES,
  ANNOUNCEMENT_ROLES,
  ACADEMIC_SETUP_ROLES,
  TIME_TABLE_ROLES,
} from "@/config/roleAccess";

// Public leaf pages (lazy-loaded)
const LoginPage = lazy(() => import("@/features/auth/pages/LoginPage"));
const SignupPage = lazy(() => import("@/features/auth/pages/SignupPage"));
const ActivateAccountPage = lazy(
  () => import("@/features/auth/pages/ActivateAccountPage"),
);
const CreatePasswordPage = lazy(
  () => import("@/features/auth/pages/CreatePasswordPage"),
);
const ActivationSuccessPage = lazy(
  () => import("@/features/auth/pages/ActivationSuccessPage"),
);

// Protected leaf pages (lazy-loaded to reduce main bundle)
const DashboardPage = lazy(() =>
  import("@/features/dashboard/pages/DashboardPage").then((m) => ({
    default: m.DashboardPage,
  })),
);
const TeachersPage = lazy(() => import("@/features/teachers/pages/TeachersPage"));
const AddTeachers = lazy(() => import("@/features/teachers/pages/AddTeachers"));
const TeachersBulkUpload = lazy(
  () => import("@/features/teachers/pages/TeachersBulkUpload"),
);
const TeacherClassMappingPage = lazy(
  () => import("@/features/teachers/pages/TeacherClassMappingPage"),
);
const StudentsPage = lazy(() => import("@/features/students/pages/StudentsPage"));
const AddStudents = lazy(() => import("@/features/students/pages/AddStudents"));
const StudentsBulkUpload = lazy(
  () => import("@/features/students/pages/StudentsBulkUpload"),
);
const ContentCreatorsPage = lazy(
  () => import("@/features/contentCreators/pages/ContentCreatorsPage"),
);
const AddContentCreators = lazy(
  () => import("@/features/contentCreators/pages/AddContentCreators"),
);
const ContentCreatorsInfo = lazy(
  () => import("@/features/contentCreators/pages/ContentCreatorsInfo"),
);
const ContentCreatorsBulkUpload = lazy(
  () => import("@/features/contentCreators/pages/ContentCreatorsBulkUpload"),
);
const TenantsPage = lazy(
  () => import("@/features/school/pages/TenantsPage"),
);
const AddTenantPage = lazy(
  () => import("@/features/school/pages/AddTenantPage"),
);
const EditTenantPage = lazy(
  () => import("@/features/school/pages/EditTenantPage"),
);
const InstitutionsPage = lazy(
  () => import("@/features/institutions/pages/InstitutionsPage"),
);
const AddInstitutionPage = lazy(
  () => import("@/features/institutions/pages/AddInstitutionPage"),
);
const EditInstitutionPage = lazy(
  () => import("@/features/institutions/pages/EditInstitutionPage"),
);
const InstitutionAdminsPage = lazy(
  () => import("@/features/institutions/pages/InstitutionAdminsPage"),
);
const AnnouncementsPage = lazy(
  () => import("@/features/announcements/pages/AnnouncementsPage"),
);
const AcademicSetupPage = lazy(
  () => import("@/features/academicSetup/pages/AcademicSetupPage"),
);
const TimeTablePage = lazy(
  () => import("@/features/timeTable/pages/TimeTablePage"),
);
const TestPage = lazy(() =>
  import("@/features/test/pages/TestPage").then((m) => ({
    default: m.TestPage,
  })),
);
const AddTestPage = lazy(() =>
  import("@/features/test/pages/AddTestPage").then((m) => ({
    default: m.AddTestPage,
  })),
);
const TestViewPage = lazy(() =>
  import("@/features/test/pages/TestViewPage").then((m) => ({
    default: m.TestViewPage,
  })),
);
const ContentStudioListPage = lazy(() =>
  import("@/features/contentStudio/pages/ContentStudioListPage").then((m) => ({
    default: m.ContentStudioListPage,
  })),
);
const UploadBookPage = lazy(() =>
  import("@/features/contentStudio/pages/UploadBookPage").then((m) => ({
    default: m.UploadBookPage,
  })),
);
const ContentStructurePage = lazy(() =>
  import("@/features/contentStudio/pages/ContentStructurePage").then((m) => ({
    default: m.ContentStructurePage,
  })),
);
const EditMetadataPage = lazy(() =>
  import("@/features/contentStudio/pages/EditMetadataPage").then((m) => ({
    default: m.EditMetadataPage,
  })),
);
const ContentPage = lazy(() =>
  import("@/features/contentStudio/pages/ContentPage").then((m) => ({
    default: m.ContentPage,
  })),
);
const AIReviewStatusPage = lazy(() =>
  import("@/features/contentStudio/pages/AIReviewStatusPage").then((m) => ({
    default: m.AIReviewStatusPage,
  })),
);
const HumanReviewPage = lazy(() =>
  import("@/features/contentStudio/pages/HumanReviewPage").then((m) => ({
    default: m.HumanReviewPage,
  })),
);
const HumanReviewStatusPage = lazy(() =>
  import("@/features/contentStudio/pages/HumanReviewStatusPage").then((m) => ({
    default: m.HumanReviewStatusPage,
  })),
);
import { ContentStudioLayout } from "@/features/contentStudio/components/ContentStudioLayout";

const NotFoundPage = lazy(
  () => import("@/shared/components/common/NotFoundPage"),
);
const UnauthorizedPage = lazy(
  () => import("@/shared/components/common/UnauthorizedPage"),
);

const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<Loading />}>{children}</Suspense>
);

export const routes: RouteObject[] = [
  {
    path: "/",
    element: <Navigate to="/dashboard" replace />,
  },
  {
    element: <AuthLayout />, // 👈 common layout
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "/login",
        element: (
          <PublicRoute>
            <SuspenseWrapper>
              <LoginPage />
            </SuspenseWrapper>
          </PublicRoute>
        ),
      },
      {
        path: "/signup",
        element: (
          <PublicRoute>
            <SuspenseWrapper>
              <SignupPage />
            </SuspenseWrapper>
          </PublicRoute>
        ),
      },
      {
        path: "/personalize",
        element: (
          <SuspenseWrapper>
            <PersonalizedLearning />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/account/activate",
        element: (
          <SuspenseWrapper>
            <ActivateAccountPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/create-password",
        element: (
          <SuspenseWrapper>
            <CreatePasswordPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: "/success",
        element: (
          <SuspenseWrapper>
            <ActivationSuccessPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  //   ✅ Protected routes with DashboardLayout
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <LanguageProvider>
          <DashboardLayout /> {/* ✅ Layout with Outlet */}
        </LanguageProvider>
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "dashboard",
        element: (
          <RoleBasedRoute roles={[UserRole.ADMIN, UserRole.PLATFORM_ADMIN, UserRole.CREATOR]}>
            <SuspenseWrapper>
              <DashboardPage /> {/* ✅ Just the dashboard content, no layout */}
            </SuspenseWrapper>
          </RoleBasedRoute>
        ),
      },
      // Future routes
      {
        path: "teachers",
        element: (
          <RoleBasedRoute roles={[UserRole.ADMIN, UserRole.PLATFORM_ADMIN]}>
            <TeachersLayout />
          </RoleBasedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <TeachersPage />
              </SuspenseWrapper>
            ), // default list
          },

          {
            path: "add-teachers",
            element: (
              <SuspenseWrapper>
                <AddTeachers />
              </SuspenseWrapper>
            ),
          },
          {
            path: "edit/:id",
            element: (
              <SuspenseWrapper>
                <AddTeachers />
              </SuspenseWrapper>
            ),
          },
          {
            path: "bulk-upload",
            element: (
              <SuspenseWrapper>
                <TeachersBulkUpload />
              </SuspenseWrapper>
            ),
          },
          {
            path: "class-mapping",
            element: (
              <SuspenseWrapper>
                <TeacherClassMappingPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "add-teachers/teachers-list",
            element: <Navigate to="/teachers" replace />,
          },
          {
            path: "view-profile",
            element: <Navigate to="/teachers" replace />,
          },
        ],
      },
      {
        path: "students",
        element: (
          <RoleBasedRoute roles={[UserRole.ADMIN, UserRole.PLATFORM_ADMIN]}>
            <StudentsLayout />
          </RoleBasedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <StudentsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "add-students",
            element: (
              <SuspenseWrapper>
                <AddStudents />
              </SuspenseWrapper>
            ),
          },
          {
            path: "edit/:id",
            element: (
              <SuspenseWrapper>
                <AddStudents />
              </SuspenseWrapper>
            ),
          },
          {
            path: "bulk-upload",
            element: (
              <SuspenseWrapper>
                <StudentsBulkUpload />
              </SuspenseWrapper>
            ),
          },
          {
            path: "view-profile",
            element: <Navigate to="/students" replace />,
          },
        ],
      },
      {
        path: "tenants",
        element: (
          <RoleBasedRoute roles={SCHOOL_ROLES}>
            <TenantsLayout />
          </RoleBasedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <TenantsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "add",
            element: (
              <SuspenseWrapper>
                <AddTenantPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "edit/:id",
            element: (
              <SuspenseWrapper>
                <EditTenantPage />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      {
        path: "institutions",
        element: (
          <RoleBasedRoute
            roles={INSTITUTION_ROLES}
            deniedRoles={INSTITUTION_DENIED_ROLES}
          >
            <InstitutionLayout />
          </RoleBasedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <InstitutionsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "add",
            element: (
              <SuspenseWrapper>
                <AddInstitutionPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "edit/:id",
            element: (
              <SuspenseWrapper>
                <EditInstitutionPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "admins",
            element: (
              <SuspenseWrapper>
                <InstitutionAdminsPage />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      {
        path: "content-creators",
        element: (
          <RoleBasedRoute roles={[UserRole.ADMIN, UserRole.PLATFORM_ADMIN]}>
            <ContentCreatorsLayout />
          </RoleBasedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <ContentCreatorsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "add-content-creators",
            element: (
              <SuspenseWrapper>
                <AddContentCreators />
              </SuspenseWrapper>
            ),
          },
          {
            path: "edit/:id",
            element: (
              <SuspenseWrapper>
                <AddContentCreators />
              </SuspenseWrapper>
            ),
          },
          {
            path: "bulk-upload",
            element: (
              <SuspenseWrapper>
                <ContentCreatorsBulkUpload />
              </SuspenseWrapper>
            ),
          },
          {
            path: "view-profile",
            element: (
              <SuspenseWrapper>
                <ContentCreatorsInfo />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      {
        path: "test-prep",
        children: [
          {
            index: true,
            element: (
              <RoleBasedRoute roles={[UserRole.CREATOR]}>
                <SuspenseWrapper>
                  <TestPage />
                </SuspenseWrapper>
              </RoleBasedRoute>
            ),
          },
          {
            path: "add",
            element: (
              <RoleBasedRoute roles={[UserRole.CREATOR]}>
                <SuspenseWrapper>
                  <AddTestPage />
                </SuspenseWrapper>
              </RoleBasedRoute>
            ),
          },
          {
            path: ":textbookId",
            element: (
              <RoleBasedRoute roles={[UserRole.CREATOR]}>
                <SuspenseWrapper>
                  <TestViewPage />
                </SuspenseWrapper>
              </RoleBasedRoute>
            ),
          },
        ],
      },
      {
        path: "content-studio",
        element: (
          <RoleBasedRoute roles={[UserRole.CREATOR, UserRole.ADMIN, UserRole.PLATFORM_ADMIN]}>
            <ContentStudioLayout />
          </RoleBasedRoute>
        ),
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <ContentStudioListPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: "new",
            element: (
              <SuspenseWrapper>
                <UploadBookPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: ":id/contentstructure",
            element: (
              <SuspenseWrapper>
                <ContentStructurePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: ":id/metadata",
            element: (
              <SuspenseWrapper>
                <EditMetadataPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: ":id/content",
            element: (
              <SuspenseWrapper>
                <ContentPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: ":id/aireviewstatus",
            element: (
              <SuspenseWrapper>
                <AIReviewStatusPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: ":id/humanreview",
            element: (
              <SuspenseWrapper>
                <HumanReviewPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: ":id/humanreviewstatus",
            element: (
              <SuspenseWrapper>
                <HumanReviewStatusPage />
              </SuspenseWrapper>
            ),
          },
        ],
      },
      {
        path: "academic-setup",
        element: (
          <RoleBasedRoute roles={ACADEMIC_SETUP_ROLES}>
            <SuspenseWrapper>
              <AcademicSetupPage />
            </SuspenseWrapper>
          </RoleBasedRoute>
        ),
      },
      {
        path: "time-table",
        element: (
          <RoleBasedRoute roles={TIME_TABLE_ROLES}>
            <SuspenseWrapper>
              <TimeTablePage />
            </SuspenseWrapper>
          </RoleBasedRoute>
        ),
      },
      {
        path: "announcements",
        element: (
          <RoleBasedRoute roles={ANNOUNCEMENT_ROLES}>
            <SuspenseWrapper>
              <AnnouncementsPage />
            </SuspenseWrapper>
          </RoleBasedRoute>
        ),
      },
    ],
  },
  {
    path: "unauthorized",
    element: (
      <SuspenseWrapper>
        <UnauthorizedPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: "*",
    element: (
      <SuspenseWrapper>
        <NotFoundPage />
      </SuspenseWrapper>
    ),
  },
];
